import { API_BASE_URL } from '../config/settings';

// Stage 2 types
export type ExtractionType = 'hook' | 'angle' | 'loop' | 'stake' | 'structure' | 'framework';
export type QualityTier = 'premium' | 'standard' | 'skip';

export interface Resource {
  id: string;
  type: 'notes' | 'knowledge' | 'multimedia';
  title: string;
  content: string | null;
  sourceUrl: string | null;
  source: string | null;
  resourceCategory: string;
  tags: string[];
  project: string | null;
  humanReview: boolean;
  hasMultimedia: boolean;
  isKnowledgeEnabled: boolean;
  metadata: Record<string, unknown>;
  customFields: Record<string, unknown>;
  completionScore: number;
  processingStatus: string;
  createdAt: number;
  updatedAt: number;
  // Stage 2 - Extracted elements
  parentId?: string | null;
  extractionType?: ExtractionType | null;
  qualityTier?: QualityTier | null;
}

export interface ResourceFilters {
  type?: string;
  category?: string;
  humanReview?: boolean;
  source?: string;
  project?: string;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  // Stage 2 filters
  parentId?: string | null;
  extractionType?: ExtractionType;
  qualityTier?: QualityTier;
  hasChildren?: boolean;
}

const headers = { 'Content-Type': 'application/json' };

export const resourcesApi = {
  // List resources with filters
  list: async (filters: ResourceFilters = {}): Promise<{ resources: Resource[], total: number, success: boolean }> => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.category) params.append('category', filters.category);
    if (filters.humanReview !== undefined) params.append('humanReview', String(filters.humanReview));
    if (filters.source) params.append('source', filters.source);
    if (filters.project) params.append('project', filters.project);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset) params.append('offset', String(filters.offset));
    // Stage 2 filters
    if (filters.parentId !== undefined) {
      params.append('parentId', filters.parentId === null ? 'null' : filters.parentId);
    }
    if (filters.extractionType) params.append('extractionType', filters.extractionType);
    if (filters.qualityTier) params.append('qualityTier', filters.qualityTier);
    if (filters.hasChildren !== undefined) params.append('hasChildren', String(filters.hasChildren));

    const queryString = params.toString();
    const url = queryString ? `${API_BASE_URL}/resources?${queryString}` : `${API_BASE_URL}/resources`;
    const response = await fetch(url, { headers });
    return response.json();
  },

  // Get single resource
  get: async (id: string): Promise<{ data: Resource, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}`, { headers });
    return response.json();
  },

  // Create resource
  create: async (data: Partial<Resource>): Promise<{ data: Resource, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    return response.json();
  },

  // Update resource
  update: async (id: string, data: Partial<Resource>): Promise<{ data: Resource, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    return response.json();
  },

  // Delete resource
  delete: async (id: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}`, {
      method: 'DELETE',
      headers
    });
    return response.json();
  },

  // Approve resource (mark as reviewed, boost score)
  approve: async (id: string): Promise<{ data: Resource, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}/approve`, {
      method: 'POST',
      headers,
      body: '{}'
    });
    return response.json();
  },

  // Reject resource with optional reason
  reject: async (id: string, reason?: string): Promise<{ data: Resource, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}/reject`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason })
    });
    return response.json();
  },

  // Review with level (trash, wrong, ok, gold)
  review: async (
    id: string,
    level: 'trash' | 'wrong' | 'ok' | 'gold',
    reason?: string
  ): Promise<{ data?: Resource, success: boolean, action?: string, retrainTriggered?: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}/review`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ level, reason })
    });
    return response.json();
  },

  // Bulk operations
  bulk: async (
    ids: string[],
    action: 'delete' | 'approve' | 'reject' | 'updateCategory' | 'updateTags',
    options?: { category?: string; tags?: string[]; reason?: string }
  ): Promise<{
    data: { processed: number; succeeded: number; failed: number; results: { id: string; success: boolean; error?: string }[] };
    success: boolean;
  }> => {
    const response = await fetch(`${API_BASE_URL}/resources/bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ids, action, ...options })
    });
    return response.json();
  },

  // Semantic search
  search: async (query: string, filters: Partial<ResourceFilters> = {}): Promise<{ resources: Resource[], total: number, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/search`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, ...filters })
    });
    return response.json();
  },

  // Get pending review items
  pending: async (limit = 50, offset = 0): Promise<{ resources: Resource[], total: number, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/pending?limit=${limit}&offset=${offset}`, { headers });
    return response.json();
  },

  // Get stats
  stats: async (): Promise<{
    data: {
      total: number;
      byType: Record<string, number>;
      byCategory: Record<string, number>;
      pendingReview: number;
    },
    success: boolean
  }> => {
    const response = await fetch(`${API_BASE_URL}/resources/stats`, { headers });
    return response.json();
  },

  // Export resources
  exportData: async (format: 'json' | 'csv' = 'json'): Promise<Blob | { resources: Resource[]; exported: number; success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/export?format=${format}`, { headers });
    if (format === 'csv') {
      return response.blob();
    }
    return response.json();
  },

  // Get related resources
  related: async (id: string, limit = 5): Promise<{ data: { related: Resource[] }, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}/related?limit=${limit}`, { headers });
    return response.json();
  },

  // Q&A with RAG
  ask: async (question: string, limit = 5): Promise<{
    data: {
      question: string;
      answer: string;
      sources: Array<{ id: string; title: string; score: number; excerpt: string }>;
    },
    success: boolean,
    error?: string
  }> => {
    const response = await fetch(`${API_BASE_URL}/resources/ask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ question, limit })
    });
    return response.json();
  },

  // Generate mind map
  mindmap: async (topic: string, limit = 8): Promise<{
    data: {
      topic: string;
      markdown: string;
      sources: Array<{ id: string; title: string }>;
    },
    success: boolean,
    error?: string
  }> => {
    const response = await fetch(`${API_BASE_URL}/resources/mindmap`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ topic, limit })
    });
    return response.json();
  },

  // Find hidden connections
  discoveries: async (options: { limit?: number; minScore?: number } = {}): Promise<{
    data: {
      discoveries: Array<{
        resource1: { id: string; title: string; category: string };
        resource2: { id: string; title: string; category: string };
        score: number;
      }>;
      total: number;
    },
    success: boolean,
    error?: string
  }> => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', String(options.limit));
    if (options.minScore) params.append('minScore', String(options.minScore));
    const queryString = params.toString();
    const url = queryString ? `${API_BASE_URL}/resources/discoveries?${queryString}` : `${API_BASE_URL}/resources/discoveries`;
    const response = await fetch(url, { headers });
    return response.json();
  },

  // Ingest new content
  ingest: async (data: {
    content?: string;
    title?: string;
    sourceUrl?: string;
    type?: 'notes' | 'knowledge' | 'multimedia';
    resourceCategory?: string;
    tags?: string[];
    project?: string;
    prefill?: boolean;
    dedupe?: 'skip' | 'update' | 'none';
  }): Promise<{
    data: {
      id: string;
      type: string;
      status: string;
      prefilled: boolean;
      classification?: Record<string, unknown>;
      isDuplicate: boolean;
    },
    success: boolean
  }> => {
    const response = await fetch(`${API_BASE_URL}/ingest`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    return response.json();
  },

  // ========================================
  // STAGE 2 - Content Creation Monster
  // ========================================

  // Get children (extracted elements) of a parent document
  getChildren: async (parentId: string): Promise<{ resources: Resource[], success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${parentId}/children`, { headers });
    return response.json();
  },

  // Count children of a parent document
  countChildren: async (parentId: string): Promise<{ count: number, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${parentId}/children/count`, { headers });
    return response.json();
  },

  // Delete resource with children handling
  deleteWithChildren: async (
    id: string,
    deleteChildren: boolean
  ): Promise<{ data: { deleted: number, orphaned: number }, success: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/resources/${id}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ deleteChildren })
    });
    return response.json();
  },
};
